import moment from "moment";
import store from "@/store";
import governanceStoreModule from "./governanceStoreModule";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export const GOVERNANCE_STORE = "governance";

export function registerGovernanceStore() {
  if (!store.hasModule(GOVERNANCE_STORE)) {
    store.registerModule(GOVERNANCE_STORE, governanceStoreModule);
  }
}

export function unregisterGovernanceStore() {
  if (store.hasModule(GOVERNANCE_STORE)) {
    store.unregisterModule(GOVERNANCE_STORE);
  }
}

// Shared labels / formatters for the Governance pages.
export default {
  data() {
    return {
      governanceTypeLabels: {
        meeting: "Meeting",
        board_meeting: "Board Meeting",
        agm: "AGM",
        training: "Training",
        other: "Other",
      },
      governanceStatusVariants: {
        draft: "light-secondary",
        scheduled: "light-primary",
        cancelled: "light-danger",
        completed: "light-success",
      },
      governanceResponseVariants: {
        pending: "light-secondary",
        accepted: "light-success",
        declined: "light-danger",
        tentative: "light-warning",
      },
    };
  },
  methods: {
    govTypeLabel(type) {
      return this.governanceTypeLabels[type] || type || "-";
    },
    govStatusVariant(status) {
      return this.governanceStatusVariants[status] || "light-secondary";
    },
    govResponseVariant(response) {
      return this.governanceResponseVariants[response] || "light-secondary";
    },
    govCapitalize(value) {
      if (!value) return "-";
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    govFormatDate(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "-";
    },
    govFormatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";
    },
    govFormatRange(event) {
      if (!event || !event.start_at) return "-";
      const start = moment(event.start_at);
      if (event.is_all_day) {
        const endDay = event.end_at ? moment(event.end_at) : null;
        return endDay && !endDay.isSame(start, "day")
          ? `${start.format("DD/MM/YYYY")} - ${endDay.format("DD/MM/YYYY")} (all day)`
          : `${start.format("DD/MM/YYYY")} (all day)`;
      }
      if (!event.end_at) return start.format("DD/MM/YYYY HH:mm");
      const end = moment(event.end_at);
      return end.isSame(start, "day")
        ? `${start.format("DD/MM/YYYY HH:mm")} - ${end.format("HH:mm")}`
        : `${start.format("DD/MM/YYYY HH:mm")} - ${end.format("DD/MM/YYYY HH:mm")}`;
    },
    /** Parts of an event; older payloads without parts fall back to one part from the event. */
    govParts(event) {
      if (!event) return [];
      if (event.parts && event.parts.length) return event.parts;
      if (!event.start_at) return [];
      return [{
        sequence_no: 1,
        start_at: event.start_at,
        end_at: event.end_at,
        location_label: event.location || (event.meeting_url ? "Online" : null),
        meeting_url: event.meeting_url,
      }];
    },
    /** "Title - Part N" when there are several parts, as for training events. */
    govPartTitle(event, part, idx) {
      const count = this.govParts(event).length;
      const n = (part && part.sequence_no) || idx + 1;
      return count > 1 ? `${event.title} - Part ${n}` : event.title;
    },
    govFormatPartRange(part, isAllDay) {
      return this.govFormatRange({ start_at: part.start_at, end_at: part.end_at, is_all_day: isAllDay });
    },
    govHref(url) {
      if (!url) return null;
      return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    },
    govToast(title, variant = "success", text = "") {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === "success" ? "CheckIcon" : "AlertTriangleIcon",
          variant,
          text,
        },
      });
    },
    govErrorMessage(error, fallback = "Something went wrong") {
      const data = error && error.response && error.response.data;
      if (data && data.errors) {
        const first = Object.values(data.errors)[0];
        if (Array.isArray(first) && first.length) return first[0];
      }
      return (data && data.message) || fallback;
    },
    govSaveBlob(blob, fileName) {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    govDownloadMaterial(material) {
      if (material.external_url) {
        window.open(material.external_url, "_blank");
        return;
      }
      store
        .dispatch("governance/downloadMaterial", material.id)
        .then((res) => this.govSaveBlob(res.data, material.file_name || material.title))
        .catch((error) => this.govToast("Download failed", "danger", this.govErrorMessage(error)));
    },
  },
};
